import React, { useState, useEffect } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { LayoutDashboard, Utensils, Dumbbell, ScanLine, Calendar, Gift, LogOut, Settings, Heart, Gem, Shield } from 'lucide-react';
import { useUser } from '../context/UserContext';
import { useSubscription } from '../context/SubscriptionContext';
import NotificationToast from './NotificationToast';

import LoadingOverlay from './LoadingOverlay';
import AnnouncementBanner from './AnnouncementBanner';

const Layout = () => {
    const location = useLocation();
    const navigate = useNavigate();

    const { currentTier, getTierInfo, getRemainingScans, canAccessMetabolicTable, getDaysUntilExpiry, presentPaywall, expiryDate } = useSubscription();
    // Safe tierInfo — never undefined, even during initialisation
    const tierInfo = React.useMemo(() => {
        try { return getTierInfo() || { icon: '🌱', name: 'Starter', color: 'text-gray-400' }; }
        catch { return { icon: '🌱', name: 'Starter', color: 'text-gray-400' }; }
    }, [getTierInfo]);

    // Check if we're on auth-related pages (no nav should show)
    const authPaths = ['/auth', '/signin', '/onboarding', '/privacy', '/health-disclaimer', '/contact', '/faq', '/subscription/success', '/subscription/cancel'];
    const isAuthPage = authPaths.some(path => location.pathname === path || location.pathname.startsWith(path));

    // Super Admin check from UserContext
    const { userProfile } = useUser();
    const isSuperAdmin = userProfile?.isSuperAdmin === true;

    const [notification, setNotification] = useState(null);
    const [loading, setLoading] = useState(false);

    // Trigger RevenueCat native paywall directly
    const handleUpgradeRequest = async (featureName = null) => {
        setLoading(true);
        try {
            const result = await presentPaywall();
            if (result && result.result === 'NOT_PRESENTED') {
                setNotification({
                    message: 'Subscriptions are currently only available on the native iOS app.',
                    type: 'info'
                });
            }
        } catch (error) {
            console.error('Paywall error:', error);
            let errorMessage = 'An error occurred while loading the paywall.';

            if (error.code === 23 || (error.message && error.message.includes('23'))) {
                errorMessage = 'Error code 23 indicates RevenueCat needs a Paywall designed in the dashboard before this works.';
            } else if (error.message) {
                errorMessage = error.message;
            }

            setNotification({
                message: errorMessage,
                type: 'error'
            });
        } finally {
            setLoading(false);
        }
    };

    // SUBSCRIPTION REMINDER POPUP
    // For non-subscribers: show upgrade modal after 3 minutes in app
    // Also shows on every app open for Starter users
    // NEVER show on auth pages (let user sign up first!)
    // NEVER show to Super Admin
    useEffect(() => {
        // Super Admin bypass - never show upgrade prompts
        if (isSuperAdmin) return;

        // Only for STARTER tier (non-paying users)
        if (currentTier !== 'STARTER') return;

        // NEVER show upgrade modal on auth pages - let user create account first!
        if (isAuthPage) return;

        // Check if we should show on app open (once per session)
        const sessionShown = sessionStorage.getItem('calobite_upgrade_shown_session');
        if (!sessionShown) {
            // Show after 2 seconds on app open (give time for UI to load)
            const openTimer = setTimeout(() => {
                handleUpgradeRequest();
                sessionStorage.setItem('calobite_upgrade_shown_session', 'true');
            }, 2000);
            return () => clearTimeout(openTimer);
        }

        // Also show after 3 minutes of usage (reminder popup)
        const usageTimer = setTimeout(() => {
            handleUpgradeRequest('timeReminder');
        }, 3 * 60 * 1000); // 3 minutes

        return () => clearTimeout(usageTimer);
    }, [currentTier, isAuthPage]);

    // Full mode nav items (Profile removed — accessible via Settings > Edit Profile)
    const navItems = [
        { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard', color: 'text-primary' },
        { icon: ScanLine, label: 'Scan', path: '/scan', color: 'text-green-400' },
        { icon: Utensils, label: 'Meals', path: '/meals', color: 'text-emerald-400' },
        { icon: Dumbbell, label: 'Exercise', path: '/exercise', color: 'text-primary' },
        { icon: Calendar, label: 'Schedule', path: '/schedule', color: 'text-pink-400' },
        { icon: Gift, label: 'Rewards', path: '/referrals', color: 'text-yellow-400' },
        { icon: Settings, label: 'Settings', path: '/settings', color: 'text-gray-400' },
    ];
    const mobileNavItems = navItems.slice(0, 5);

    const handleLogout = () => {
        if (window.confirm('Logout?')) {
            localStorage.removeItem('calobite_user');
            window.location.href = '/';
        }
    };

    return (
        <div className="min-h-screen bg-slate-950 text-slate-50 flex flex-col md:flex-row overflow-hidden">
            {/* Skip link for keyboard navigation - WCAG 2.1 */}
            <a
                href="#main-content"
                className="skip-link sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-1/2 focus:transform focus:-translate-x-1/2 focus:z-[9999] focus:px-6 focus:py-3 focus:bg-primary-500 focus:text-white focus:font-bold focus:rounded-xl focus:shadow-lg"
            >
                Skip to main content
            </a>

            {/* Desktop Sidebar */}
            <aside
                role="complementary"
                aria-label="Main navigation"
                className="hidden md:flex flex-col w-56 bg-slate-900/80 backdrop-blur-xl border-r border-white/5 h-screen sticky top-0 z-50"
            >
                <div className="p-5">
                    <button
                        onClick={() => navigate('/scan')}
                        className="flex items-center gap-2 hover:opacity-80 transition-opacity cursor-pointer"
                        title="Go to Scan"
                    >
                        <img src="/assets/logos/Logo_Trans.png" alt="CaloBite" className="w-8 h-8 object-contain" />
                        <h1 className="text-xl font-poppins font-semibold tracking-tight">
                            <span className="text-primary">Calo</span><span className="text-white">Bite</span>
                        </h1>
                    </button>

                </div>

                <nav aria-label="Primary navigation" className="flex-1 px-3 space-y-1 overflow-y-auto custom-scrollbar">
                    {navItems.map((item) => {
                        const isActive = location.pathname === item.path;
                        return (
                            <button
                                key={item.path}
                                onClick={() => navigate(item.path)}
                                aria-label={`Navigate to ${item.label}`}
                                aria-current={isActive ? 'page' : undefined}
                                className={`w-full flex items-center gap-3 px-3 py-2.5 min-h-[44px] rounded-xl transition-all group ${isActive
                                    ? 'bg-white/5 border border-white/10'
                                    : 'hover:bg-white/5'}`}
                            >
                                <item.icon className={`w-5 h-5 ${isActive ? item.color : 'text-slate-500 group-hover:text-slate-300'}`} aria-hidden="true" />
                                <span className={`font-bold text-sm ${isActive ? 'text-white' : 'text-slate-400 group-hover:text-slate-200'}`}>
                                    {item.label}
                                </span>
                                {isActive && <div className={`ml-auto w-1.5 h-1.5 rounded-full ${item.color.replace('text-', 'bg-')}`} aria-hidden="true" />}
                            </button>
                        );
                    })}
                </nav>

                <div className="p-3 border-t border-white/5">
                    {/* Super Admin Button - Desktop sidebar */}
                    {isSuperAdmin && (
                        <button
                            onClick={() => navigate('/admin')}
                            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-red-500 hover:bg-red-500/10 mb-2 transition-colors"
                        >
                            <Shield className="w-5 h-5" />
                            <span className="font-bold text-sm">Super Admin</span>
                            <span className="ml-auto w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                        </button>
                    )}
                    <button onClick={handleLogout} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-slate-400 hover:bg-red-500/10 hover:text-red-400 transition-colors">
                        <LogOut className="w-5 h-5" />
                        <span className="font-bold text-sm">Logout</span>
                    </button>
                </div>
            </aside>

            {/* Mobile Header - only show in app pages */}
            {!isAuthPage && (
                <header
                    role="banner"
                    className="md:hidden bg-slate-900/80 backdrop-blur-xl border-b border-white/5 p-3 sticky top-0 z-50 flex justify-between items-center"
                >
                    <button
                        onClick={() => navigate('/scan')}
                        aria-label="Go to scan page"
                        className="flex items-center gap-2 hover:opacity-80 transition-opacity min-h-[44px] flex-shrink-0"
                    >
                        <img src="/assets/logos/Logo_Trans.png" alt="" className="w-7 h-7 object-contain" aria-hidden="true" />
                        <h1 className="text-lg font-poppins font-semibold">
                            <span className="text-primary">Calo</span><span className="text-white">Bite</span>
                        </h1>

                    </button>
                    <div className="flex items-center gap-1 flex-shrink min-w-0" role="toolbar" aria-label="Quick actions">
                        {/* Subscription Button */}
                        <button
                            onClick={() => {
                                if (currentTier === 'FREE' || currentTier === 'STARTER') {
                                    handleUpgradeRequest();
                                } else {
                                    setNotification({
                                        message: (() => {
                                            const icon = tierInfo?.icon || '🔥';
                                            const name = tierInfo?.name || 'Pro';
                                            if (isSuperAdmin) return `${icon} ${name} • Admin Access`;
                                            if (!expiryDate)  return `${icon} ${name} • Active`;
                                            const fmt = new Date(expiryDate).toLocaleDateString('en-AU', {
                                                day: 'numeric', month: 'short', year: 'numeric'
                                            });
                                            return `${icon} ${name} • Renews ${fmt}`;
                                        })(),
                                        type: 'info'
                                    });
                                }
                            }}
                            aria-label={`Subscription: ${tierInfo?.name || 'Starter'}`}
                            className={`min-w-[44px] min-h-[44px] p-2 flex items-center justify-center relative ${currentTier === 'FREE' || currentTier === 'STARTER' ? 'text-gray-400' :
                                currentTier === 'CORE' ? 'text-primary' :
                                    currentTier === 'PRO' ? 'text-orange-400' : 'text-purple-400'
                                }`}
                        >
                            <Gem className="w-5 h-5" aria-hidden="true" />
                            {currentTier !== 'FREE' && currentTier !== 'STARTER' && (
                                <span className={`absolute top-0 right-0 rounded-full font-black flex items-center justify-center text-white leading-none ${currentTier === 'PRO' ? 'bg-orange-500 text-[7px] px-[3px] py-[2px] min-w-[18px] h-[14px]' : currentTier === 'CORE' ? 'bg-primary w-3.5 h-3.5 text-xs' : 'bg-purple-500 w-3.5 h-3.5 text-xs'}`}>
                                    {currentTier === 'CORE' ? 'C' : currentTier === 'PRO' ? 'PRO' : '👑'}
                                </span>
                            )}
                        </button>
                        <button onClick={() => navigate('/favorites')} aria-label="Favorites" className="min-w-[44px] min-h-[44px] p-2 flex items-center justify-center text-rose-400 hover:text-rose-300"><Heart className="w-5 h-5" aria-hidden="true" /></button>
                        <button onClick={() => navigate('/referrals')} aria-label="Rewards and referrals" className="min-w-[44px] min-h-[44px] p-2 flex items-center justify-center text-yellow-400 hover:text-yellow-300"><Gift className="w-5 h-5" aria-hidden="true" /></button>
                        <button onClick={() => navigate('/settings')} aria-label="Settings" className="min-w-[44px] min-h-[44px] p-2 flex items-center justify-center text-slate-400 hover:text-white"><Settings className="w-5 h-5" aria-hidden="true" /></button>
                        {/* Super Admin Button - Only visible for cal.ajordan@outlook.com */}
                        {isSuperAdmin && (
                            <button
                                onClick={() => navigate('/admin')}
                                aria-label="Super Admin Dashboard"
                                className="min-w-[44px] min-h-[44px] p-2 flex items-center justify-center text-red-500 hover:text-red-400 relative"
                            >
                                <Shield className="w-5 h-5" aria-hidden="true" />
                                <span className="absolute top-0 right-0 w-2.5 h-2.5 bg-red-500 rounded-full animate-pulse" aria-hidden="true" />
                            </button>
                        )}
                        <button onClick={() => handleLogout()} aria-label="Logout" className="min-w-[44px] min-h-[44px] p-2 flex items-center justify-center text-slate-400 hover:text-white"><LogOut className="w-5 h-5" aria-hidden="true" /></button>
                    </div>
                </header>
            )}
            {/* Announcement Banner - show on app pages */}
            {!isAuthPage && <AnnouncementBanner />}

            {/* Main Content */}
            <main
                id="main-content"
                role="main"
                aria-label="Main content"
                className="flex-1 overflow-y-auto h-[calc(100vh-56px)] md:h-screen relative custom-scrollbar"
            >
                <div className="fixed top-0 left-0 w-full h-full overflow-hidden pointer-events-none z-0" aria-hidden="true">
                    <div className="absolute top-[-20%] right-[-10%] w-[400px] h-[400px] bg-purple-600/10 rounded-full blur-[100px]" />
                    <div className="absolute bottom-[-20%] left-[-10%] w-[400px] h-[400px] bg-primary/10 rounded-full blur-[100px]" />
                </div>

                <div className="relative z-10 p-4 md:p-6 max-w-6xl mx-auto pb-20 md:pb-6">
                    <Outlet context={{
                        setNotification,
                        setLoading,
                        handleUpgradeRequest,
                        // Aliases so all pages that call setShowUpgradeModal(true) / setUpgradeFeature()
                        // correctly trigger the RevenueCat paywall — no need to patch each page individually
                        setShowUpgradeModal: (show) => { if (show) handleUpgradeRequest(); },
                        setUpgradeFeature: () => { }, // feature name unused — paywall handles it
                    }} />
                </div>
            </main>

            {/* Mobile Bottom Nav - only show in app pages */}
            {!isAuthPage && (
                <nav
                    aria-label="Mobile navigation"
                    className="md:hidden fixed bottom-0 left-0 right-0 bg-slate-900/95 backdrop-blur-xl border-t border-white/5 z-50 px-2 py-2 safe-area-bottom"
                >
                    {/* Reorganize: Dashboard, Meals, [SCAN], Exercise, Schedule */}
                    <div className="flex justify-around items-end">
                        {/* Left side items */}
                        {mobileNavItems.filter(item => item.label !== 'Scan').slice(0, 2).map((item) => {
                            const isActive = location.pathname === item.path;
                            return (
                                <button
                                    key={item.path}
                                    onClick={() => navigate(item.path)}
                                    aria-label={`Navigate to ${item.label}`}
                                    aria-current={isActive ? 'page' : undefined}
                                    className={`flex flex-col items-center gap-0.5 min-w-[48px] min-h-[48px] py-1 transition-all ${isActive ? '' : 'opacity-50'}`}
                                >
                                    <div className={`p-1.5 rounded-full ${isActive ? 'bg-white/10' : ''}`}>
                                        <item.icon className={`w-5 h-5 ${isActive ? item.color : 'text-white'}`} aria-hidden="true" />
                                    </div>
                                    <span className={`text-xs font-bold ${isActive ? 'text-white' : 'text-gray-400'}`}>{item.label}</span>
                                </button>
                            );
                        })}

                        {/* CENTER: Scan Button - Distinguished */}
                        {(() => {
                            const scanItem = mobileNavItems.find(item => item.label === 'Scan');
                            if (!scanItem) return null;
                            const isActive = location.pathname === scanItem.path;
                            return (
                                <div className="relative -mt-6">
                                    {/* Glow effect */}
                                    <div className="absolute inset-0 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full blur-lg opacity-60 scale-110" aria-hidden="true" />
                                    {/* Pulse ring */}
                                    <div className="absolute inset-0 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full animate-ping opacity-20" aria-hidden="true" />
                                    <button
                                        onClick={() => navigate(scanItem.path)}
                                        aria-label="Scan food"
                                        aria-current={isActive ? 'page' : undefined}
                                        className={`relative flex flex-col items-center justify-center w-16 h-16 rounded-full transition-all border-4 border-slate-900 shadow-xl shadow-green-500/30 ${isActive
                                            ? 'bg-gradient-to-br from-green-500 to-emerald-600'
                                            : 'bg-gradient-to-br from-green-600 to-emerald-700 hover:scale-105'
                                            }`}
                                    >
                                        <ScanLine className="w-7 h-7 text-white" aria-hidden="true" />
                                    </button>
                                    <span className={`text-xs font-bold text-center w-full block mt-1 ${isActive ? 'text-green-400' : 'text-gray-400'}`} aria-hidden="true">Scan</span>
                                </div>
                            );
                        })()}

                        {/* Right side items */}
                        {mobileNavItems.filter(item => item.label !== 'Scan').slice(2).map((item) => {
                            const isActive = location.pathname === item.path;
                            return (
                                <button
                                    key={item.path}
                                    onClick={() => navigate(item.path)}
                                    aria-label={`Navigate to ${item.label}`}
                                    aria-current={isActive ? 'page' : undefined}
                                    className={`flex flex-col items-center gap-0.5 min-w-[48px] min-h-[48px] py-1 transition-all ${isActive ? '' : 'opacity-50'}`}
                                >
                                    <div className={`p-1.5 rounded-full ${isActive ? 'bg-white/10' : ''}`}>
                                        <item.icon className={`w-5 h-5 ${isActive ? item.color : 'text-white'}`} aria-hidden="true" />
                                    </div>
                                    <span className={`text-xs font-bold ${isActive ? 'text-white' : 'text-gray-400'}`}>{item.label}</span>
                                </button>
                            );
                        })}
                    </div>
                </nav>
            )}

            {/* Overlays */}
            {notification && <NotificationToast notification={notification} onClose={() => setNotification(null)} />}
            {loading && <LoadingOverlay />}
        </div>
    );
};

export default Layout;

